<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-0vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <!-- Hero -->
    <section id="hero">
      <div class="overflow-hidden mt-10">
        <div class="absolute top-0 right-1/4 w-[600px] h-[600px]  blur-3xl -translate-y-1/2 pointer-events-none"></div>
        <div class="absolute bottom-0 left-1/3 w-[500px] h-[500px]  blur-3xl translate-y-1/3 pointer-events-none"></div>

        <div class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col items-center gap-y-8 relative z-10">

          <span class="inline-flex items-center gap-x-2 bg-[#ff5f89]/3 text-[#ff9bc4] text-xs font-semibold px-4 py-2 rounded-full border border-#ff5f89/20">
            <span class="w-2 h-2 bg-[#FF3696] rounded-full animate-pulse shrink-0"></span>
            <span class="text-gradient-primary">{{ t('pages.campaigns.agents.hero.badge') }}</span>
          </span>

          <h1 class="text-[32px] leading-[42px] lg:text-[54px] lg:leading-[62px] font-bold font-markPro text-[#051A21] text-center tracking-[-0.1rem] max-w-[820px]">
            {{ t('pages.campaigns.agents.hero.title_1') }}
            <span class="text-gradient-primary"> {{ t('pages.campaigns.agents.hero.title_em') }}</span>
            {{ t('pages.campaigns.agents.hero.title_2') }}
          </h1>

          <p class="text-[#8B9AA1] text-lg lg:text-xl font-semibold text-center max-w-[640px]">
            {{ t('pages.campaigns.agents.hero.subtitle') }}
          </p>

          <div class="flex flex-col sm:flex-row gap-4 mt-2">
            <a href="#apply" class="btn btn-lg btn-primary whitespace-nowrap">
              {{ t('pages.campaigns.agents.hero.cta_primary') }}
            </a>
            <a href="#deal" class="btn btn-lg border-2 border-gray-300/60 whitespace-nowrap transition-colors">
              {{ t('pages.campaigns.agents.hero.cta_secondary') }}
            </a>
          </div>

          <div class="grid grid-cols-2 lg:grid-cols-4 gap-12 w-full border-t border-white/10 pt-10 mt-2">
            <div v-for="stat in heroStats" :key="stat.value" class="text-center">
              <p class="text-3xl lg:text-4xl font-bold font-markPro text-gradient-primary">{{ stat.value }}</p>
              <p class="text-sm text-[#8B9AA1] font-semibold mt-1">{{ stat.label }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Products: What you'll be selling -->
    <section id="products" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center pt-10">
        <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{ t('pages.campaigns.agents.products.kicker') }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.campaigns.agents.products.heading') }}
        </h2>
        <p class="text-lighter font-semibold max-w-[600px]">
          {{ t('pages.campaigns.agents.products.lead') }}
        </p>
      </div>
      <div class="grid md:grid-cols-3 gap-6 w-full">
        <div
          v-for="product in productItems"
          :key="product.title"
          class="bg-white rounded-2xl border border-gray-100 drop-shadow p-8 flex flex-col gap-y-4"
        >
          <div class="text-2xl mt-2 mb-4"><span class="bg-[#f7f9fb] border-1 border-[#ecedf0] rounded-xl px-3 py-2">{{ product.icon }}</span></div>
          <h3 class="font-markPro font-bold text-dark text-xl">{{ product.title }}</h3>
          <p class="text-lighter text-sm font-semibold leading-relaxed">{{ product.text }}</p>
        </div>
      </div>
    </section>

    <!-- Deal -->
    <section id="deal">
      <div class="bg-[#F7F9FB] py-10">
        <div class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col gap-y-10">
          <div class="flex flex-col gap-y-4 text-center items-center">
            <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{ t('pages.campaigns.agents.deal.kicker') }}</span>
            <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem] max-w-[640px]">
              {{ t('pages.campaigns.agents.deal.heading') }}
            </h2>
            <p class="text-lighter font-semibold max-w-[580px]">
              {{ t('pages.campaigns.agents.deal.lead') }}
            </p>
          </div>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-[#051A21] rounded-2xl p-8 flex flex-col gap-y-5">
              <h3 class="font-markPro font-bold text-white text-xs uppercase tracking-widest">{{ t('pages.campaigns.agents.deal.commission.title') }}</h3>
              <p class="text-7xl font-bold font-markPro text-gradient-primary leading-none">{{ t('pages.campaigns.agents.deal.commission.value') }}</p>
              <p class="text-[#8B9AA1] font-semibold leading-relaxed text-sm">{{ t('pages.campaigns.agents.deal.commission.text') }}</p>
            </div>
            <div class="bg-[#E3EEF7] border-2 border-[#d2e3f1] rounded-2xl p-8 flex flex-col gap-y-5 relative overflow-hidden">
              <span class="absolute top-5.5 right-5 bg-gradient-primary text-white text-xs font-semibold px-3.5 py-2 rounded-full">{{ t('pages.campaigns.agents.deal.retainer.pill') }}</span>
              <h3 class="font-markPro font-bold text-lighter text-xs uppercase tracking-widest">{{ t('pages.campaigns.agents.deal.retainer.title') }}</h3>
              <p class="text-5xl lg:text-6xl font-bold font-markPro text-dark leading-none">{{ t('pages.campaigns.agents.deal.retainer.value') }}</p>
              <p class="text-lighter font-semibold leading-relaxed text-sm">{{ t('pages.campaigns.agents.deal.retainer.text') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Ramp table -->
    <section id="ramp" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{ t('pages.campaigns.agents.ramp.kicker') }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem] max-w-[800px]">
          {{ t('pages.campaigns.agents.ramp.heading') }}
        </h2>
        <p class="text-lighter font-semibold max-w-[800px]">
          {{ t('pages.campaigns.agents.ramp.lead') }}
        </p>
      </div>

      <div class="w-full overflow-x-auto rounded-2xl border border-gray-100 drop-shadow">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr class="border-b-2 border-gray-100">
              <th
                v-for="header in rampHeaders"
                :key="header"
                class="text-left py-4 px-5 text-xs font-semibold text-white uppercase tracking-widest whitespace-nowrap bg-dark"
              >{{ header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in rampRows"
              :key="i"
              :class="['border-b border-gray-100 last:border-0', i === rampRows.length - 1 ? 'bg-[#e3eef7]' : 'hover:bg-gray-50']"
            >
              <td
                v-for="(cell, j) in row"
                :key="j"
                :class="['py-4 px-5 text-sm font-semibold', j === 0 ? 'text-dark font-bold' : 'text-lighter']"
              >{{ cell }}</td>
            </tr>
          </tbody>
        </table>
        <p class="text-xs text-lighter font-semibold leading-relaxed p-6 bg-[#f7f9fb]">{{ t('pages.campaigns.agents.ramp.note') }}</p>
      </div>

      <ul class="flex flex-col gap-y-5 w-full -mt-6">
        <li v-for="check in rampChecks" :key="check.strong" class="flex items-start gap-x-3">
          <div class="w-6 h-6 bg-gradient-to-br from-[#FF3696] to-[#FF7C53] rounded-full flex items-center justify-center shrink-0 mt-0.5">
            <i class="bi bi-check text-white text-xs"></i>
          </div>
          <p class="text-lighter font-semibold">
            <strong class="text-dark">{{ check.strong }}</strong>
            {{ check.text }}
          </p>
        </li>
      </ul>
    </section>

    <!-- Steps -->
    <section id="steps">
      <div class="bg-[#F7F9FB] pt-10 pb-5">
        <div class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col gap-y-10">
          <div class="flex flex-col gap-y-4 text-center items-center">
            <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{ t('pages.campaigns.agents.steps.kicker') }}</span>
            <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
              {{ t('pages.campaigns.agents.steps.heading') }}
            </h2>
          </div>
          <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              v-for="(step, i) in stepItems"
              :key="step.title"
              class="bg-white rounded-2xl drop-shadow p-6 flex flex-col gap-y-3"
            >
              <span class="text-4xl font-bold font-markPro text-gradient-primary leading-none">{{ i + 1 }}</span>
              <h3 class="font-markPro font-bold text-dark text-lg">{{ step.title }}</h3>
              <p class="text-lighter text-sm font-semibold leading-relaxed">{{ step.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Who we're looking for -->
    <section id="who" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{ t('pages.campaigns.agents.who.kicker') }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.campaigns.agents.who.heading') }}
        </h2>
      </div>
      <div class="grid md:grid-cols-3 gap-6 w-full">
        <div
          v-for="profile in whoItems"
          :key="profile.title"
          class="bg-white rounded-2xl border border-gray-100 drop-shadow p-8 flex flex-col gap-y-4"
        >
          <span class="text-4xl leading-none">{{ profile.icon }}</span>
          <h3 class="font-markPro font-bold text-dark text-xl">{{ profile.title }}</h3>
          <p class="text-lighter text-sm font-semibold leading-relaxed">{{ profile.text }}</p>
        </div>
      </div>
    </section>

    <!-- CTA / Apply -->
    <section id="apply" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div class="parallax" data-parallax data-parallax-speed="0.05" data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]" src="/images/bg-visual-left-top.webp" alt="" />
            <img class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]" src="/images/bg-visual-right-top.webp" alt="" />
          </div>
        </div>
        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h2 class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('pages.campaigns.agents.cta.heading') }}
          </h2>
          <p class="text-lg lg:text-[24px] text-lighter font-semibold max-w-[600px]">
            {{ t('pages.campaigns.agents.cta.text') }}
          </p>
          <a
            href="https://forms.gle/HoQBETJEbNmmQGpw5"
            target="_blank"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4"
          >
            <span>{{ t('pages.campaigns.agents.cta.button') }}</span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
          <p class="text-xs text-lighter/60 font-semibold mt-2">{{ t('pages.campaigns.agents.cta.small') }}</p>
        </div>
      </div>
    </section>

  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const { t, tm } = useI18n()

const heroStats = computed(() => tm('pages.campaigns.agents.hero.stats') as { value: string; label: string }[])
const productItems = computed(() => tm('pages.campaigns.agents.products.items') as { icon: string; title: string; text: string }[])
const rampHeaders = computed(() => tm('pages.campaigns.agents.ramp.table_headers') as string[])
const rampRows = computed(() => tm('pages.campaigns.agents.ramp.table_rows') as string[][])
const rampChecks = computed(() => tm('pages.campaigns.agents.ramp.checks') as { strong: string; text: string }[])
const stepItems = computed(() => tm('pages.campaigns.agents.steps.items') as { title: string; text: string }[])
const whoItems = computed(() => tm('pages.campaigns.agents.who.items') as { icon: string; title: string; text: string }[])

useHead(computed(() => ({
  title: t('pages.campaigns.agents.seo.title'),
  meta: [{ name: 'description', content: t('pages.campaigns.agents.seo.description') }],
})))
</script>

<style scoped>
@reference "@/assets/css/main.css";
</style>
